import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { isEmpty } from 'lodash';
import { Route, Switch } from 'react-router-dom';
import { ROUTES } from '../../Constants';
import Waiting from './Waiting';
import NoMoreJobs from './NoMoreJobs';
import Call from './call';
import Feedback from './Feedback';
import Instructions from './Instructions';
import DevTwilioPanel from './DevTwilioPanel';
import {
  liveCallStatus as liveCallStatusAction,
  getJobAgentSow as getJobAgentSowAction
} from '../../lib/redux';
import getActiveJobSelector from '../../lib/redux/selectors/app/active-job';

const Job = () => (
  <>
    {process.env.NODE_ENV === 'development' && <DevTwilioPanel />}
    <Switch>
      <Route exact path={ROUTES.JOB_FEEDBACK} component={Feedback} />
      <Route exact path={ROUTES.JOB_CALL} component={Call} />
      <Route exact path={ROUTES.JOB_INSTRUCTIONS} component={Instructions} />
      <Route exact path={ROUTES.NO_MORE_TASKS} component={NoMoreJobs} />
      <Route component={Waiting} />
    </Switch>
  </>
);

/**
 * Initialises a `liveCallStatus` socket whenever a new `currentTask` appears in redux.
 * Cleans up the socket when the task is removed.
 */
const withLiveCallStatusForTask = compose(
  connect(
    state => ({
      currentTask: state.tasks.currentTask.task,
      agentIsVendor: state.me.agentIsVendor,
      activeJob: getActiveJobSelector(state)
    }),
    {
      liveCallStatus: liveCallStatusAction,
      getJobAgentSow: getJobAgentSowAction
    }
  ),

  Component => {
    function WithLiveCallStatusForTask({
      currentTask,
      liveCallStatus,
      agentIsVendor,
      getJobAgentSow,
      activeJob,
      history,
      endJob,
      resetAgentActivity
    }) {
      const abortWebsocketConnection = useRef();
      useEffect(() => {
        (async () => {
          if (!agentIsVendor) {
            const { isSigned } = await getJobAgentSow(activeJob.jobId);

            if (!isSigned) {
              history.push(ROUTES.SOW, { nextRoute: ROUTES.JOB });
            }
          }
        })();

        return async () => {
          await endJob();
        };
      }, []);

      useEffect(() => {
        (async () => {
          const hasCurrentTask = !isEmpty(currentTask);
          const websocketConnected = abortWebsocketConnection.current;

          if (!websocketConnected && hasCurrentTask) {
            abortWebsocketConnection.current = await liveCallStatus(
              currentTask.taskId,
              currentTask.callId
            );
          } else if (websocketConnected && !hasCurrentTask) {
            abortWebsocketConnection.current();
            abortWebsocketConnection.current = null;
          }
        })();

        (async () => {
          if (isEmpty(currentTask)) await resetAgentActivity(activeJob.jobId);
        })();

        return () => abortWebsocketConnection.current && abortWebsocketConnection.current();
      }, [currentTask]);

      return <Component />;
    }

    WithLiveCallStatusForTask.propTypes = {
      currentTask: PropTypes.shape({
        taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        callId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
      }).isRequired,
      liveCallStatus: PropTypes.func.isRequired,
      agentIsVendor: PropTypes.bool.isRequired,
      getJobAgentSow: PropTypes.func.isRequired,
      activeJob: PropTypes.shape({
        jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
      }).isRequired,
      history: PropTypes.shape({
        push: PropTypes.func.isRequired
      }).isRequired,
      endJob: PropTypes.func.isRequired,
      resetAgentActivity: PropTypes.func.isRequired
    };

    return WithLiveCallStatusForTask;
  }
);

export default withLiveCallStatusForTask(Job);
